import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const ORIGEN_OPCIONES = ['Manual', 'Meta', 'WhatsApp', 'Web'];

// Mapeo de origen del lead -> fuente_medio del contacto (mismo catálogo que usa Contactos.js)
const ORIGEN_A_FUENTE = {
  Manual: 'Autogeneración',
  Meta: 'Campañas digitales',
  WhatsApp: 'Mensaje WhatsApp',
  Web: 'Portal inmobiliario',
};

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function pad(n) { return n.toString().padStart(2, '0'); }
function fechaISO(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function horaActual() { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; }
function hhmm(t) { return t ? t.slice(0, 5) : ''; }

export default function Leads({ miRol, miAgente }) {
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [agentesTodos, setAgentesTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [filtroDesarrollo, setFiltroDesarrollo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState(formVacio());
  const [asignando, setAsignando] = useState(null); // lead siendo asignado
  const [asesorSel, setAsesorSel] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [msgAsignar, setMsgAsignar] = useState('');
  const [sugerenciaAuto, setSugerenciaAuto] = useState(null); // { correo, nombre } | 'sin_guardia'

  function formVacio() {
    return { nombre: '', telefono: '', correo: '', desarrollo_id: '', origen: 'Manual', notas: '' };
  }

  const esAdmin = miRol === 'Super Admin' || miRol === 'Admin' || miRol === 'Sub Admin';

  useEffect(() => { cargarDesarrollos(); cargarAgentes(); }, []);
  useEffect(() => { cargarLeads(); }, [filtroDesarrollo]);

  const cargarDesarrollos = async () => {
    const { data } = await supabase.from('desarrollos').select('id, nombre').order('nombre');
    setDesarrollos(data || []);
  };

  const cargarAgentes = async () => {
    const { data } = await supabase.from('agentes').select('correo, nombre, apellidos, desarrollos').eq('rol', 'Agente').eq('activo', true);
    setAgentesTodos(data || []);
  };

  const cargarLeads = async () => {
    setLoading(true);
    let query = supabase.from('leads').select('*, desarrollos(nombre)').order('created_at', { ascending: false });
    if (filtroDesarrollo) query = query.eq('desarrollo_id', filtroDesarrollo);
    const { data } = await query;
    setLeads(data || []);
    setLoading(false);
  };

  const agentesDelDesarrollo = (desarrolloNombre) =>
    agentesTodos.filter(a => (a.desarrollos || []).includes(desarrolloNombre));

  const handleAgregar = async () => {
    if (!form.nombre.trim() || !form.desarrollo_id) return;
    setGuardando(true);
    await supabase.from('leads').insert([{
      nombre: form.nombre.trim(), telefono: form.telefono.trim(), correo: form.correo.trim(),
      desarrollo_id: form.desarrollo_id, origen: form.origen, notas: form.notas,
      creado_por: miAgente?.correo || null,
    }]);
    setGuardando(false);
    setShowForm(false); setForm(formVacio());
    cargarLeads();
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    await supabase.from('leads').delete().eq('id', id);
    cargarLeads();
  };

  // FIX: determina qué asesor está en turno AHORA para un desarrollo, según
  // la guardia cargada para hoy. Regla: antes de la hora_fin del turno AM
  // (de ese desarrollo, ese día) -> asesor(es) AM. Desde la hora_inicio del
  // turno PM -> asesor(es) PM (vigente hasta 23:59). Si hay 2 asesores en el
  // turno, alterna (round robin) según cuántos leads ya se le asignaron hoy
  // en esa guardia. Si no hay guardia cargada, regresa null (sin sugerencia).
  const obtenerAsesorEnTurno = async (desarrolloId) => {
    const hoy = fechaISO(new Date());
    const ahora = horaActual();
    const { data: guardiasHoy } = await supabase
      .from('guardias')
      .select('id, turno, hora_inicio, hora_fin, guardia_asesores(agente_correo, orden)')
      .eq('desarrollo_id', desarrolloId)
      .eq('fecha', hoy);

    if (!guardiasHoy || guardiasHoy.length === 0) return null;

    const gAM = guardiasHoy.find(g => g.turno === 'AM');
    const gPM = guardiasHoy.find(g => g.turno === 'PM');

    let guardiaActiva = null;
    if (gAM && ahora < gAM.hora_fin) guardiaActiva = gAM;
    else if (gPM && ahora >= gPM.hora_inicio) guardiaActiva = gPM;
    else if (gAM && !gPM) guardiaActiva = gAM; // solo hay AM cargado, cubre todo el día
    else if (gPM && !gAM) guardiaActiva = gPM; // solo hay PM cargado, cubre todo el día

    if (!guardiaActiva) return null;

    const asesores = (guardiaActiva.guardia_asesores || []).sort((a, b) => a.orden - b.orden);
    if (asesores.length === 0) return null;
    if (asesores.length === 1) {
      const ag = agentesTodos.find(a => a.correo === asesores[0].agente_correo);
      return { correo: asesores[0].agente_correo, nombre: ag ? `${ag.nombre} ${ag.apellidos || ''}`.trim() : asesores[0].agente_correo };
    }

    // Round robin entre los 2: contar leads ya convertidos hoy con cada asesor_asignado
    // (usamos negocios.creado_por como proxy de "a quién se le asignó" ya que el lead
    // se elimina al convertirse; negocios.created_at::date = hoy y desarrollo = este)
    const correos = asesores.map(a => a.agente_correo);
    const inicioHoy = `${hoy}T00:00:00`;
    const { data: negociosHoy } = await supabase
      .from('negocios')
      .select('asesor_correo')
      .in('asesor_correo', correos)
      .gte('created_at', inicioHoy);

    const conteo = {};
    correos.forEach(c => { conteo[c] = 0; });
    (negociosHoy || []).forEach(n => { if (conteo[n.asesor_correo] !== undefined) conteo[n.asesor_correo]++; });

    const correoElegido = correos.reduce((menor, c) => conteo[c] < conteo[menor] ? c : menor, correos[0]);
    const ag = agentesTodos.find(a => a.correo === correoElegido);
    return { correo: correoElegido, nombre: ag ? `${ag.nombre} ${ag.apellidos || ''}`.trim() : correoElegido };
  };

  const abrirAsignar = async (lead) => {
    setAsignando(lead);
    setAsesorSel('');
    setMsgAsignar('');
    setSugerenciaAuto(null);
    const sugerencia = await obtenerAsesorEnTurno(lead.desarrollo_id);
    setSugerenciaAuto(sugerencia || 'sin_guardia');
  };

  const cerrarAsignar = () => { setAsignando(null); setAsesorSel(''); setMsgAsignar(''); setSugerenciaAuto(null); };

  const usarSugerencia = () => {
    if (sugerenciaAuto && sugerenciaAuto !== 'sin_guardia') setAsesorSel(sugerenciaAuto.correo);
  };

  // FIX: al confirmar la asignación:
  // 1) busca o crea el Contacto (evita duplicar por correo/teléfono)
  // 2) crea el Negocio ligado a ese contacto en etapa "Cotización"
  // 3) elimina el lead de la bandeja (ya quedó convertido)
  const handleConfirmarAsignacion = async () => {
    if (!asesorSel) { setMsgAsignar('Selecciona un asesor'); return; }
    setProcesando(true);
    setMsgAsignar('');

    const lead = asignando;
    const agente = agentesTodos.find(a => a.correo === asesorSel);
    const nombreAsesor = agente ? `${agente.nombre} ${agente.apellidos || ''}`.trim() : '';
    const desarrolloNombre = lead.desarrollos?.nombre || desarrollos.find(d => d.id === lead.desarrollo_id)?.nombre || '';

    const partes = (lead.nombre || '').trim().split(' ');
    const nombres = partes[0] || '';
    const apellidos = partes.slice(1).join(' ') || '';
    const soloDigitos = (lead.telefono || '').replace(/\D/g, '').slice(-10);
    const telefonoFormateado = soloDigitos ? `+52 ${soloDigitos}` : '';

    try {
      // Buscar contacto existente por correo o teléfono
      let contactoId = null;
      let fuenteContacto = ORIGEN_A_FUENTE[lead.origen] || 'Autogeneración';
      if (lead.correo || telefonoFormateado) {
        const orParts = [];
        if (lead.correo) orParts.push(`correo.eq.${lead.correo}`);
        if (telefonoFormateado) orParts.push(`telefono.eq.${telefonoFormateado}`);
        const { data: existente } = await supabase.from('contactos').select('*').or(orParts.join(',')).limit(1);
        if (existente && existente.length > 0) { contactoId = existente[0].id; fuenteContacto = existente[0].fuente_medio; }
      }

      if (contactoId) {
        // Reactivar y actualizar asesor si ya existía
        await supabase.from('contactos').update({
          asesor_ventas: nombreAsesor, creado_por: asesorSel,
          ultima_actividad: new Date().toISOString(), vigencia_activa: true,
        }).eq('id', contactoId);
      } else {
        const { data: nuevoContacto, error: errContacto } = await supabase.from('contactos').insert([{
          nombres, apellidos, correo: lead.correo || '', telefono: telefonoFormateado,
          desarrollo: desarrolloNombre, estatus: 'Prospecto', tiempo_compra: '', tipo_compra: '',
          asesor_ventas: nombreAsesor, presupuesto: null, fuente_medio: ORIGEN_A_FUENTE[lead.origen] || 'Autogeneración',
          razon_no_compra: '', creado_por: asesorSel,
          ultima_actividad: new Date().toISOString(), vigencia_activa: true,
        }]).select().single();
        if (errContacto) { setMsgAsignar('Error al crear contacto: ' + errContacto.message); setProcesando(false); return; }
        contactoId = nuevoContacto.id;
      }

      // Crear el negocio
      const { error: errNegocio } = await supabase.from('negocios').insert([{
        nombre: `${lead.nombre} — ${desarrolloNombre}`,
        contacto_id: contactoId, valor: 0, desarrollo: desarrolloNombre,
        // Showroom ya llegó al punto de venta: el negocio empieza en Cita.
        ...(fuenteContacto === 'Showroom'
          ? { etapa: 'Cita', inicio_directo_cita: true, cita_en: new Date().toISOString() }
          : { etapa: 'Cotización', cotizacion_en: new Date().toISOString() }),
        asesor_ventas: nombreAsesor, asesor_correo: asesorSel, creado_por: asesorSel,
        ultima_actividad: new Date().toISOString(), vigencia_activa: true,
      }]);
      if (errNegocio) { setMsgAsignar('Error al crear negocio: ' + errNegocio.message); setProcesando(false); return; }

      // Eliminar el lead ya convertido
      await supabase.from('leads').delete().eq('id', lead.id);

      setProcesando(false);
      cerrarAsignar();
      cargarLeads();
    } catch (err) {
      setMsgAsignar('Error inesperado: ' + err.message);
      setProcesando(false);
    }
  };

  const leadsFiltrados = leads.filter(l =>
    !buscar || l.nombre?.toLowerCase().includes(buscar.toLowerCase()) ||
    l.correo?.toLowerCase().includes(buscar.toLowerCase()) || l.telefono?.includes(buscar)
  );

  if (!esAdmin) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No tienes acceso a este módulo</div>;
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Leads</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>{leads.length} leads pendientes de asignar</div>
        </div>
        <button onClick={() => { setForm(formVacio()); setShowForm(true); }}
          style={{ ...btnPrimary, padding: isMobile ? '10px 14px' : '8px 16px', fontSize: isMobile ? '14px' : '13px' }}>
          + {isMobile ? 'Nuevo' : 'Agregar lead'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input placeholder='Buscar...' value={buscar} onChange={e => setBuscar(e.target.value)}
          style={{ padding: '8px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', width: isMobile ? '100%' : '200px' }} />
        <select value={filtroDesarrollo} onChange={e => setFiltroDesarrollo(e.target.value)}
          style={{ padding: '8px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
          <option value=''>Todos los desarrollos</option>
          {desarrollos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Cargando...</div>
      ) : leadsFiltrados.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Sin leads pendientes</div>
      ) : (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
          {leadsFiltrados.map((l, i) => (
            <div key={l.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: i < leadsFiltrados.length - 1 ? '0.5px solid #f0f0f0' : 'none' }}>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{l.nombre}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{l.telefono} {l.correo && `· ${l.correo}`}</div>
              </div>
              <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#f0f0f0', color: '#555' }}>{l.desarrollos?.nombre || '—'}</span>
              <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#EEF2FF', color: '#4338CA' }}>{l.origen}</span>
              <div style={{ display: 'flex', gap: '8px', marginLeft: isMobile ? 0 : 'auto' }}>
                <button onClick={() => abrirAsignar(l)}
                  style={{ padding: '8px 14px', background: '#2E7D4F', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                  Asignar asesor
                </button>
                <button onClick={() => handleEliminar(l.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: '16px' }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Panel agregar lead */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: isMobile ? '100%' : '380px', height: '100vh', background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>Agregar lead</h3>
            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#888' }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Nombre completo *</label>
              <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Teléfono</label>
              <input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} style={inputStyle} placeholder='10 dígitos' />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Correo</label>
              <input value={form.correo} onChange={e => setForm({ ...form, correo: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Desarrollo *</label>
              <select value={form.desarrollo_id} onChange={e => setForm({ ...form, desarrollo_id: e.target.value })} style={inputStyle}>
                <option value=''>Selecciona...</option>
                {desarrollos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Origen</label>
              <select value={form.origen} onChange={e => setForm({ ...form, origen: e.target.value })} style={inputStyle}>
                {ORIGEN_OPCIONES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Notas</label>
              <textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '0.5px solid #f0f0f0' }}>
            <button onClick={handleAgregar} disabled={guardando || !form.nombre.trim() || !form.desarrollo_id}
              style={{ ...btnPrimary, width: '100%', padding: '14px', justifyContent: 'center', fontSize: '14px' }}>
              {guardando ? 'Guardando...' : 'Agregar lead'}
            </button>
          </div>
        </div>
      )}

      {/* Modal asignar asesor */}
      {asignando && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={cerrarAsignar}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '380px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', margin: 0, color: '#1a1a2e' }}>{asignando.nombre}</h3>
              <button onClick={cerrarAsignar} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '1.25rem' }}>{asignando.desarrollos?.nombre}</div>

            {sugerenciaAuto === null ? (
              <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '14px' }}>Buscando asesor en turno...</div>
            ) : sugerenciaAuto === 'sin_guardia' ? (
              <div style={{ padding: '10px', background: '#FFF8E1', color: '#856404', borderRadius: '8px', fontSize: '12px', marginBottom: '14px' }}>
                No hay guardia cargada para hoy en este desarrollo. Asigna manualmente.
              </div>
            ) : (
              <div style={{ padding: '10px', background: '#EAF3DE', borderRadius: '8px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#27500A' }}>Sugerido por guardia:</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#27500A' }}>{sugerenciaAuto.nombre}</div>
                </div>
                <button onClick={usarSugerencia}
                  style={{ padding: '6px 12px', background: '#27500A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Usar
                </button>
              </div>
            )}

            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Asesor</label>
              <select value={asesorSel} onChange={e => setAsesorSel(e.target.value)} style={inputStyle}>
                <option value=''>Selecciona...</option>
                {agentesDelDesarrollo(asignando.desarrollos?.nombre).map(a => (
                  <option key={a.correo} value={a.correo}>{a.nombre} {a.apellidos}</option>
                ))}
              </select>
            </div>

            {msgAsignar && (
              <div style={{ padding: '10px', background: '#FCEBEB', color: '#A32D2D', borderRadius: '8px', fontSize: '12px', marginBottom: '14px' }}>{msgAsignar}</div>
            )}

            <button onClick={handleConfirmarAsignacion} disabled={procesando}
              style={{ ...btnPrimary, width: '100%', padding: '12px', justifyContent: 'center', fontSize: '14px' }}>
              {procesando ? 'Procesando...' : 'Confirmar y crear contacto + negocio'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary = { background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' };
const labelStyle = { fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' };